(() => {
  const stateStore = (window.CartDrawerState = window.CartDrawerState || new Map());
  const instanceStore = (window.CartDrawerInstances = window.CartDrawerInstances || new Map());

  // Debounce utility to prevent rapid-fire cart updates
  const createDebouncer = () => {
    let pending = null;
    let isProcessing = false;

    return async (fn) => {
      if (isProcessing) {
        pending = fn;
        return;
      }
      isProcessing = true;
      try {
        await fn();
      } finally {
        isProcessing = false;
        if (pending) {
          const next = pending;
          pending = null;
          await next();
        }
      }
    };
  };

  // ─── Cart upsell sheet timing + frequency cap ──────────────────────────────
  // The sheet used to pop the instant the drawer opened (interruptive; Clarity
  // showed ~62% dismissed, 5.5% add). It now waits a short dwell, or the existing
  // 60%-scroll trigger, whichever fires first — and a visitor who actually
  // dismisses it is suppressed cross-session for UPSELL_CAP_DAYS.
  const UPSELL_DWELL_MS = 4000; // delay before auto-revealing the sheet on cart open
  // Bar split test (9 Oct 2026). Each visitor is put in group A (usual free-delivery bar) or
  // group B (own-brand line on third-party baskets) once, remembered in this browser. The
  // html class drives which bar shows; the drawer stamps the group on qualifying carts.
  const BAR_TEST_KEY = "cdBarTest";
  const barArm = (() => {
    let arm = null;
    try { arm = window.localStorage.getItem(BAR_TEST_KEY); } catch (_) {}
    if (arm !== "A" && arm !== "B") {
      arm = Math.random() < 0.5 ? "A" : "B";
      try { window.localStorage.setItem(BAR_TEST_KEY, arm); } catch (_) {}
    }
    return arm;
  })();
  try { document.documentElement.classList.toggle("cd-bar-b", barArm === "B"); } catch (_) {}
  const UPSELL_CAP_DAYS = 7;    // days to suppress the sheet after a genuine dismissal

  // ─────────────────────────────────────────────────────────────────────────
  // Shopify Web Pixels — custom events for cart upsell funnel.
  // Subscribe to these in a Custom Pixel via analytics.subscribe(name, cb).
  //   • cart_upsell_shown      — sheet opened (dwell timer or 60%-scroll trigger)
  //   • cart_upsell_dismissed  — user rejected the sheet (close X or drag handle)
  //   • cart_upsell_checkout   — user chose "continue to checkout" from the sheet
  //   • cart_upsell_added      — /cart/add.js succeeded for an upsell item
  //   • bundle_swap_shown      — 3P->bundle swap strip became visible {surface:'cart'}
  //   • bundle_swap_clicked    — Swap CTA clicked {surface:'cart'}
  //   • bundle_swap_accepted   — swap completed: tub removed + bundle added {surface:'cart'}
  //   • bundle_swap_dismissed  — "No thanks" {surface:'cart'}
  //   (the PDP module fires the same four events with surface:'pdp')
  //   Order tagging: completed swaps stamp _swap_source=ON_GSW + _swap_surface on the cart,
  //   which ride to the order as note_attributes (queryable in Shopify admin + Klaviyo).
  // Payload is intentionally PII-free; the user's email is never included.
  // ─────────────────────────────────────────────────────────────────────────
  const publishUpsellEvent = (name, detail) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === "function") {
        Shopify.analytics.publish(name, detail);
      }
      // Also dispatch a DOM CustomEvent so other listeners (e.g. GTM dataLayer
      // adapter, A/B test framework) can subscribe without polling Shopify.
      document.dispatchEvent(new CustomEvent(name, { detail }));
      // Microsoft Clarity custom event (smart event capture). The cart_upsell_*
      // events get auto-detected by Clarity's Shopify integration, but firing
      // a native Clarity event ensures capture even if that integration drops.
      if (typeof window.clarity === "function") {
        window.clarity("event", name);
      }
    } catch (err) {
      // Tracking must never break the cart. Swallow silently.
    }
  };

  // Generic publisher used for non-upsell events (ApplyCoupon, etc.). Same
  // multi-channel pattern so analytics/GTM/Clarity all receive the signal.
  const publishCartEvent = (name, detail) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === "function") {
        Shopify.analytics.publish(name, detail || {});
      }
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
      if (typeof window.clarity === "function") {
        window.clarity("event", name);
      }
    } catch (err) {}
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Shopify-native funnel storage. Cart attributes ride the cart through
  // checkout and land on the order as note_attributes — fully queryable in
  // Shopify admin via Orders filters, exports, and Reports (ShopifyQL on
  // Plus). Underscore-prefixed keys are hidden from the customer at
  // checkout. Fire-and-forget so we never block the user's actual action.
  // ─────────────────────────────────────────────────────────────────────────
  const stampCartAttributes = (attrs) => {
    try {
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ attributes: attrs }),
        credentials: "same-origin",
      }).catch(() => {});
    } catch (err) {
      // Tracking must never break the cart.
    }
  };

  const collectSheetContext = (sheet) => {
    if (!sheet) return {};
    return {
      tier: sheet.dataset.upsellTier || "",
      message: sheet.dataset.upsellMessage || "",
      remaining_cents: parseInt(sheet.dataset.remainingCents || "0", 10),
      cart_subtotal_cents: parseInt(sheet.dataset.cartSubtotalCents || "0", 10),
      cart_item_count: parseInt(sheet.dataset.cartItemCount || "0", 10),
    };
  };

  const collectCardContext = (card) => {
    if (!card) return {};
    return {
      tier: card.dataset.upsellTier || "",
      position: parseInt(card.dataset.upsellPosition || "0", 10),
      product_id: card.dataset.productId || "",
      product_handle: card.dataset.productHandle || "",
      product_title: card.dataset.productTitle || "",
      product_vendor: card.dataset.productVendor || "",
      price_cents: parseInt(card.dataset.productPriceCents || "0", 10),
    };
  };

  const collectSubscribeContext = (widget) => {
    if (!widget) return {};
    return {
      line: parseInt(widget.dataset.line || "0", 10),
      line_key: widget.dataset.lineKey || "",
      current_plan_id: widget.dataset.currentPlanId || "",
    };
  };

  const countSubscribedLines = (root) => {
    if (!root) return 0;
    // A widget with a non-empty data-current-plan-id is currently subscribed.
    return root.querySelectorAll("[data-cart-subscribe-widget][data-current-plan-id]:not([data-current-plan-id=''])").length;
  };

  // Fetch with timeout wrapper
  const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timeoutId));
  };

  const getRootUrl = () => {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    return "/";
  };

  const getSectionId = (root) => root && root.dataset && root.dataset.sectionId;

  const getDefaultInstance = () => {
    const first = document.querySelector("[data-cart-drawer]");
    if (!first) return null;
    return instanceStore.get(getSectionId(first));
  };

  const openInstance = (sectionId) => {
    if (sectionId && instanceStore.has(sectionId)) {
      instanceStore.get(sectionId).open();
      return;
    }
    const fallback = getDefaultInstance();
    if (fallback) fallback.open();
  };

  const closeInstance = (sectionId) => {
    if (sectionId && instanceStore.has(sectionId)) {
      instanceStore.get(sectionId).close();
      return;
    }
    const fallback = getDefaultInstance();
    if (fallback) fallback.close();
  };

  const bindGlobal = () => {
    if (window.__cartDrawerGlobalBound) return;
    window.__cartDrawerGlobalBound = true;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest(
        "[data-cart-drawer-open], a[href^=\"#cart-drawer-\"]"
      );
      if (trigger) {
        event.preventDefault();
        const targetId =
          trigger.getAttribute("data-cart-drawer-open") ||
          (trigger.getAttribute("href") || "").replace("#", "");
        if (targetId) {
          openInstance(targetId.replace("cart-drawer-", ""));
        } else {
          openInstance();
        }
        return;
      }

    });

    ["cart:open", "cart-drawer:open", "ajax-cart:open", "cart:toggle", "open-cart", "sidebar:open"].forEach(
      (evt) => {
        document.addEventListener(evt, () => {
          openInstance();
        });
      }
    );

    document.addEventListener("cart:close", () => {
      closeInstance();
    });
  };

  const fetchSectionHtml = async (sectionId) => {
    const url = `${getRootUrl()}?section_id=${sectionId}`;
    const response = await fetch(url, { credentials: "same-origin" });
    return response.text();
  };

  const refreshSection = async (sectionId) => {
    const currentSection = document.getElementById(`shopify-section-${sectionId}`);
    if (!currentSection) return;

    const html = await fetchSectionHtml(sectionId);
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const nextSection = parsed.getElementById(`shopify-section-${sectionId}`);
    if (!nextSection) return;

    currentSection.innerHTML = nextSection.innerHTML;
    const newRoot = currentSection.querySelector("[data-cart-drawer]");
    if (newRoot) {
      initCartDrawer(newRoot);
    }
  };

  const initCartDrawer = (root) => {
    if (!root) return;

    const sectionId = getSectionId(root);
    if (!sectionId) return;

    // Guard against double-binding: DOMContentLoaded + shopify:section:load both
    // fire initCartDrawer on the same DOM (esp. on shopifypreview.com), which
    // duplicates event listeners and made one swipe remove multiple lines.
    // refreshSection() replaces innerHTML, so a re-rendered root is a fresh node
    // without the flag and re-binds correctly.
    if (root.dataset.cdBound === "1") return;
    root.dataset.cdBound = "1";

    // The typed code lives on the CART (so Shopify applies it alongside XTRA2 / SEPTEMBER).
    // This per-tab value only drives the label; the old localStorage copy fed
    // /checkout?discount=CODE, which REPLACED every code on the cart (payday 1 Oct: 2-bag deal lost).
    const DISCOUNT_KEY = "cdDiscountCode";
    const getStoredDiscount = () => {
      try {
        const value = window.sessionStorage.getItem(DISCOUNT_KEY);
        return value ? value.trim() : "";
      } catch (e) {
        return "";
      }
    };

    const isValidDiscountCode = (code) => {
      if (!code || typeof code !== "string") return false;
      // Allow alphanumeric, hyphens, underscores only (max 50 chars)
      const pattern = /^[a-zA-Z0-9_-]{1,50}$/;
      return pattern.test(code);
    };

    const setStoredDiscount = (value) => {
      try {
        if (value && isValidDiscountCode(value)) {
          window.sessionStorage.setItem(DISCOUNT_KEY, value);
        } else {
          window.sessionStorage.removeItem(DISCOUNT_KEY);
        }
      } catch (e) {}
    };

    // Never pass ?discount= here: it replaces the cart's codes at checkout. Codes are on the cart.
    const getCheckoutUrl = () => "/checkout";

    const sameCode = (a, b) => String(a || "").toUpperCase() === String(b || "").toUpperCase();
    const cartCodes = async () => {
      const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
      return (cart.discount_codes || []).filter((d) => d && d.code);
    };
    const setCartCodes = (codes) =>
      fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: codes.join(",") }),
      }).then((r) => r.json());
    // Add one code next to the ones already on the cart. Returns true if Shopify applies it.
    const addCodeToCart = async (code) => {
      const current = await cartCodes();
      let codes = current.map((d) => d.code);
      if (!codes.some((c) => sameCode(c, code))) codes = codes.concat(code);
      const cart = await setCartCodes(codes);
      const hit = (cart.discount_codes || []).find((d) => sameCode(d.code, code));
      return !!(hit && hit.applicable);
    };
    const removeCodeFromCart = async (code) => {
      const current = await cartCodes();
      if (!current.some((d) => sameCode(d.code, code))) return;
      await setCartCodes(current.map((d) => d.code).filter((c) => !sameCode(c, code)));
    };
    const applyTypedCode = async (value, surface) => {
      setStoredDiscount(value);
      let ok = false;
      try {
        ok = await addCodeToCart(value);
      } catch (e) {}
      try {
        window.sessionStorage.setItem(DISCOUNT_KEY + ":ok", ok ? "1" : "0");
      } catch (e) {}
      publishCartEvent("apply_coupon", { code: value, surface, applicable: ok });
      await refreshSection(sectionId);
    };
    // One-off move of a code saved by the old version: put it on the cart if it still applies,
    // then forget it, so nobody carries a months-old code into checkout.
    (async () => {
      let legacy = "";
      try {
        legacy = (window.localStorage.getItem("cartDiscountCode") || "").trim();
        window.localStorage.removeItem("cartDiscountCode");
      } catch (e) {}
      if (!legacy || !isValidDiscountCode(legacy)) return;
      try {
        const before = await cartCodes();
        if (before.some((d) => sameCode(d.code, legacy))) return;
        const ok = await addCodeToCart(legacy);
        if (!ok) await removeCodeFromCart(legacy);
      } catch (e) {}
    })();

    // Per-session persistence for upsell dismissals.
    // Without this, the dismissal flag lives only in an in-memory Map keyed by sectionId —
    // it survives drawer open/close within one page load but NOT across page navigations.
    // Storing in sessionStorage means dismissal is sticky for the whole shopping session,
    // then clears naturally when the customer closes the tab. Survives Shopify cart fetches.
    const SHEET_DISMISSED_KEY = "cdUpsellSheetDismissed:" + sectionId;
    const INLINE_DISMISSED_KEY = "cdInlineUpsellDismissed:" + sectionId;
    const readSessionFlag = (key) => {
      try { return window.sessionStorage.getItem(key) === "1"; } catch (_) { return false; }
    };
    const writeSessionFlag = (key, value) => {
      try { window.sessionStorage.setItem(key, value ? "1" : "0"); } catch (_) {}
    };

    // Cross-session frequency cap. SHEET_DISMISSED_KEY (sessionStorage) clears when the
    // tab closes, so a dismisser would see the sheet again next visit. A localStorage
    // expiry timestamp suppresses re-showing for UPSELL_CAP_DAYS; the session flag stays.
    const SHEET_CAP_KEY = "cdUpsellSheetCapUntil:" + sectionId;
    const isUpsellCapped = () => {
      try { return parseInt(window.localStorage.getItem(SHEET_CAP_KEY) || "0", 10) > Date.now(); } catch (_) { return false; }
    };
    const setUpsellCap = () => {
      try { window.localStorage.setItem(SHEET_CAP_KEY, String(Date.now() + UPSELL_CAP_DAYS * 864e5)); } catch (_) {}
    };

    // Freeze the tier of the FIRST upsell shown this cart episode. _upsell_shown_tier is a
    // last-write-wins cart attribute: after an add, refreshSection re-renders and showUpsell
    // re-fires, which would otherwise rewrite the stamp with the recomputed tier (e.g. an
    // ownbrand_flip becomes closer/bestsellers), losing the original shown-tier for order
    // attribution. Adds stay reliable via _upsell_added_tier (stamped from the card at add
    // time); this only protects the shown-tier. Cleared when the cart empties (see below) so
    // a fresh cart in the same tab doesn't inherit the previous episode's tier.
    const SHEET_TIER_KEY = "cdUpsellShownTier:" + sectionId;
    const getFrozenShownTier = (currentTier) => {
      try {
        const saved = window.sessionStorage.getItem(SHEET_TIER_KEY);
        if (saved) return saved;
        if (currentTier) window.sessionStorage.setItem(SHEET_TIER_KEY, currentTier);
      } catch (_) {}
      return currentTier;
    };
    const clearFrozenShownTier = () => {
      try {
        window.sessionStorage.removeItem(SHEET_TIER_KEY);
        window.sessionStorage.removeItem(BAR_SHOWN_KEY);
        window.sessionStorage.removeItem(ADDED_HANDLES_KEY);
      } catch (_) {}
    };

    // Removal tracking: remember what came in through an upsell surface; when a cart change
    // leaves one of those products out of the cart, stamp it. Lets an order tell "took the
    // offer then removed it" apart from "took it and kept it".
    const BAR_SHOWN_KEY = "cdFlipBarShown:" + sectionId;
    const ADDED_HANDLES_KEY = "cdUpsellAddedHandles:" + sectionId;
    const rememberAddedHandles = (handles) => {
      try {
        const cur = (window.sessionStorage.getItem(ADDED_HANDLES_KEY) || "").split(",").filter(Boolean);
        handles.forEach((h) => { if (h && cur.indexOf(h) === -1) cur.push(h); });
        window.sessionStorage.setItem(ADDED_HANDLES_KEY, cur.join(","));
      } catch (_) {}
    };
    const checkUpsellRemoval = (cart) => {
      try {
        if (!cart || !Array.isArray(cart.items)) return;
        const cur = (window.sessionStorage.getItem(ADDED_HANDLES_KEY) || "").split(",").filter(Boolean);
        if (!cur.length) return;
        const inCart = cart.items.map((it) => it.handle);
        const gone = cur.filter((h) => inCart.indexOf(h) === -1);
        if (!gone.length) return;
        window.sessionStorage.setItem(ADDED_HANDLES_KEY, cur.filter((h) => gone.indexOf(h) === -1).join(","));
        publishUpsellEvent("cart_upsell_removed", { products: gone });
        stampCartAttributes({
          _upsell_removed: "1",
          _upsell_removed_product: gone.join(","),
          _upsell_removed_at: new Date().toISOString(),
        });
      } catch (_) {}
    };

    const existingState = stateStore.get(sectionId);
    const state = existingState || {
      isOpen: false,
      inlineUpsellHidden: readSessionFlag(INLINE_DISMISSED_KEY),
      hasDismissedUpsell: readSessionFlag(SHEET_DISMISSED_KEY) || isUpsellCapped(),
      showUpsellSheet: false,
      upsellTimer: null,
      scrollY: 0,
    };
    stateStore.set(sectionId, state);

    if (instanceStore.has(sectionId)) {
      const instance = instanceStore.get(sectionId);
      instance.root = root;
      instance.bind();
      instance.applyState();
      // Product page add fires cart:refresh and cart:open together, so open() decided on the
      // old markup: the bar was not stamped and the panel was judged on the basket before the
      // add. Decide again once the refreshed markup is in (12 Oct). scheduleUpsell stamps first.
      if (instance.state.isOpen) instance.scheduleUpsell();
      return;
    }

    const instance = {
      root,
      sectionId,
      state,
      open: () => {
        instance.state.isOpen = true;
        instance.state.scrollY = window.scrollY || window.pageYOffset || 0;
        // Remember what opened the drawer so focus can return there on close (a11y).
        instance.state.lastFocus = document.activeElement;
        document.documentElement.classList.add("cart-drawer-open");
        document.body.classList.add("cart-drawer-open");
        document.body.style.top = `-${instance.state.scrollY}px`;
        instance.root.classList.add("is-open");
        // Panel contents rejoin the tab order; focus moves inside once it has slid
        // in and any section re-render has settled (a rAF fires too early here).
        const panelEl = instance.root.querySelector(".cd-panel");
        if (panelEl) panelEl.removeAttribute("inert");
        // The drawer re-fetches its section HTML on open (replacing .cd-panel), so
        // attempt focus a few times until the refreshed panel is in place and focus sticks.
        var _focusTries = 0;
        var _focusIv = setInterval(function () {
          _focusTries++;
          var openRoot = document.querySelector(".cd-root.is-open");
          if (!openRoot || _focusTries > 8) { clearInterval(_focusIv); return; }
          // Target the first *visible* focusable (the theme renders duplicate close
          // buttons, one of which is visibility:hidden and can't take focus).
          var focusables = Array.prototype.filter.call(
            openRoot.querySelectorAll('[data-cart-drawer-close], a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
            function (el) { return el.offsetParent !== null && getComputedStyle(el).visibility !== "hidden"; }
          );
          var focusTarget = focusables[0];
          if (!focusTarget) {
            focusTarget = openRoot.querySelector(".cd-panel");
            if (focusTarget && !focusTarget.hasAttribute("tabindex")) focusTarget.setAttribute("tabindex", "-1");
          }
          if (!focusTarget) return;
          try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
          if (openRoot.contains(document.activeElement)) clearInterval(_focusIv);
        }, 200);
        instance.applyState();
        instance.scheduleUpsell();
        // Fire subscribe_widget_shown once per drawer-open per visible widget.
        // Use requestAnimationFrame to ensure the DOM has settled post-applyState.
        requestAnimationFrame(() => {
          const widgets = instance.root.querySelectorAll("[data-cart-subscribe-widget]");
          if (widgets.length === 0) return;
          widgets.forEach((w) => {
            publishCartEvent("subscribe_widget_shown", collectSubscribeContext(w));
          });
          stampCartAttributes({ _subscribe_offered: "1" });
        });
        // Fire bundle_swap_shown once per drawer-open per visible swap strip.
        requestAnimationFrame(() => {
          const strips = instance.root.querySelectorAll(".cbsw[data-cbsw]");
          const cbswPrev = /[?&](bsw_preview=1|preview_theme_id=)/.test(location.search);
          let shownAny = false;
          strips.forEach((s) => {
            if (s.dataset.cbswOos === "1" && !cbswPrev) { s.style.display = "none"; return; }
            if (s.style.display === "none" || s.dataset.cbswShown === "1") return;
            s.dataset.cbswShown = "1";
            shownAny = true;
            publishUpsellEvent("bundle_swap_shown", {
              surface: "cart",
              line_key: s.getAttribute("data-line-key") || "",
              bundle_id: s.getAttribute("data-bundle-id") || "",
            });
          });
          if (shownAny) {
            stampCartAttributes({ _swap_shown: "1", _swap_shown_surface: "cart", _swap_shown_at: new Date().toISOString() });
          }
        });
      },
      close: () => {
        instance.root.classList.remove("is-open");
        instance.state.isOpen = false;
        instance.state.showUpsellSheet = false;
        // Drop the panel out of the tab order while it's slid off-canvas, and
        // return focus to whatever opened it (a11y — keyboard/screen-reader users).
        const panelEl = instance.root.querySelector(".cd-panel");
        if (panelEl) panelEl.setAttribute("inert", "");
        const returnTo = instance.state.lastFocus;
        if (returnTo && typeof returnTo.focus === "function" && document.contains(returnTo)) {
          try { returnTo.focus({ preventScroll: true }); } catch (e) { returnTo.focus(); }
        }
        instance.state.lastFocus = null;
        // Do NOT reset hasDismissedUpsell here — that would un-dismiss the upsell every
        // time the drawer closes, causing it to re-pop on the next open (reported by
        // multiple customers). Dismissal is sticky for the session, restored from
        // sessionStorage on init via SHEET_DISMISSED_KEY.
        instance.clearUpsellTimer();
        document.documentElement.classList.remove("cart-drawer-open");
        document.body.classList.remove("cart-drawer-open");
        document.body.style.top = "";
        if (typeof instance.state.scrollY === "number") {
          window.scrollTo(0, instance.state.scrollY);
        }
        instance.applyState();
      },
      clearUpsellTimer: () => {
        if (instance.state.upsellTimer) {
          clearTimeout(instance.state.upsellTimer);
          instance.state.upsellTimer = null;
        }
      },
      // Bar offer (cart offer audit, 9 Oct 2026): the free-delivery bar carries the own-brand
      // offer the moment the drawer opens. Stamp that it was seen once per cart episode, so
      // bar reach can be read from orders alongside the sheet's _upsell_shown.
      stampBarShown: () => {
        if (!instance.state.isOpen) return;
        const bar = instance.root.querySelector("[data-flip-bar]");
        if (!bar) return;
        try {
          if (window.sessionStorage.getItem(BAR_SHOWN_KEY) === "1") return;
          window.sessionStorage.setItem(BAR_SHOWN_KEY, "1");
        } catch (_) {}
        // Every qualifying cart gets its test group, whichever bar it saw.
        const attrs = { _bar_test: barArm, _bar_test_at: new Date().toISOString() };
        if (barArm === "B") {
          attrs._upsell_bar_shown = "1";
          attrs._upsell_bar_mode = bar.dataset.barMode || "";
          publishUpsellEvent("cart_upsell_shown", { surface: "bar", tier: "bar_flip", bar_mode: bar.dataset.barMode || "" });
        }
        stampCartAttributes(attrs);
      },
      // Split check (12 Oct): baskets the bar test does not apply to (an own-brand item, or £80
      // and over) carry the visitor's group as _bar_group, so their orders, which the bar never
      // touched, can be checked for a 50/50 split. Kept away from _bar_test: two attribute writes
      // in flight together can lose one (seen on the TEST theme 12 Oct, both answered 200). So it
      // runs 1.5 s after the drawer last settled, never while the test's bar is in the drawer,
      // and writes only when the cart's value differs.
      scheduleBarGroup: () => {
        clearTimeout(instance.state.barGroupTimer);
        instance.state.barGroupTimer = setTimeout(() => {
          if (!instance.state.isOpen) return;
          if (instance.root.querySelector("[data-flip-bar]")) return;
          fetch("/cart.js", { credentials: "same-origin" })
            .then((r) => r.json())
            .then((cart) => {
              if (cart && cart.item_count > 0 && (cart.attributes || {})._bar_group !== barArm) {
                stampCartAttributes({ _bar_group: barArm });
              }
            })
            .catch(() => {});
        }, 1500);
      },
      scheduleUpsell: () => {
        instance.stampBarShown();
        instance.scheduleBarGroup();
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        const eligible = sheet.dataset.eligible === "true";
        if (!instance.state.isOpen) return;
        if (!eligible || instance.state.hasDismissedUpsell) {
          // A reveal timed on an earlier basket must not show the panel on this one.
          instance.clearUpsellTimer();
          instance.hideUpsell();
          return;
        }
        if (instance.state.showUpsellSheet) return;
        instance.clearUpsellTimer();
        // Non-interruptive reveal: wait a short dwell after the drawer opens (or let
        // the 60%-scroll trigger fire first — whichever comes first). Cancelled on
        // close and when the shopper heads to checkout, via clearUpsellTimer().
        instance.state.upsellTimer = setTimeout(() => {
          instance.state.upsellTimer = null;
          if (!instance.state.isOpen || instance.state.hasDismissedUpsell) return;
          instance.showUpsell();
        }, UPSELL_DWELL_MS);
      },
      triggerUpsellOnScroll: () => {
        const scrollArea = instance.root.querySelector(".cd-scroll");
        if (!scrollArea) return;
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        const eligible = sheet.dataset.eligible === "true";
        if (!eligible || instance.state.hasDismissedUpsell || instance.state.showUpsellSheet) return;

        const scrollBottom = scrollArea.scrollTop + scrollArea.clientHeight;
        const scrollHeight = scrollArea.scrollHeight;
        // Trigger when user scrolls past 60% of content
        if (scrollBottom > scrollHeight * 0.6) {
          instance.showUpsell();
        }
      },
      showUpsell: () => {
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        if (instance.state.showUpsellSheet) return;
        instance.state.showUpsellSheet = true;
        sheet.classList.add("is-visible");
        const products = Array.from(sheet.querySelectorAll("[data-upsell-card]")).map(collectCardContext);
        publishUpsellEvent("cart_upsell_shown", {
          ...collectSheetContext(sheet),
          surface: "sheet",
          products,
          product_count: products.length,
        });
        stampCartAttributes({
          _upsell_shown: "1",
          _upsell_shown_tier: getFrozenShownTier(sheet.dataset.upsellTier || ""),
          // The frozen tier above keeps the FIRST tier of the episode; this one is the tier
          // actually on screen now, so a basket that grew past £80 is no longer read as a flip.
          _upsell_last_tier: sheet.dataset.upsellTier || "",
          _upsell_shown_at: new Date().toISOString(),
        });
      },
      hideUpsell: () => {
        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (!sheet) return;
        instance.state.showUpsellSheet = false;
        sheet.classList.remove("is-visible");
        // If dismissed by user, reveal slim inline upsell
        if (instance.state.hasDismissedUpsell) {
          const slim = instance.root.querySelector("[data-slim-upsell]");
          if (slim) {
            slim.classList.remove("is-hidden");
            slim.style.display = "block";
          }
        }
      },
      applyDiscountState: () => {
        const wrapper = instance.root.querySelector("[data-discount]");
        if (!wrapper) return;
        const label = wrapper.querySelector("[data-discount-label]");
        const input = wrapper.querySelector("[data-discount-input]");
        const hint = wrapper.querySelector("[data-discount-hint]");
        const code = getStoredDiscount();
        let ok = "";
        try {
          ok = window.sessionStorage.getItem(DISCOUNT_KEY + ":ok") || "";
        } catch (e) {}
        if (label) {
          label.textContent = code ? `Discount code: ${code}` : "Have a discount code?";
        }
        if (input) {
          input.value = code || "";
        }
        if (hint) {
          hint.textContent = !code
            ? "Add it here and we'll apply it to your basket."
            : ok === "1"
              ? "Applied to your basket."
              : "This code can't be used with what's in your basket.";
        }
        wrapper.classList.toggle("has-code", !!code);
      },
      applyState: () => {
        if (instance.state.isOpen) {
          instance.root.classList.add("is-open");
        } else {
          instance.root.classList.remove("is-open");
        }

        const inlineUpsell = instance.root.querySelector("[data-inline-upsell]");
        if (inlineUpsell) {
          const eligible = inlineUpsell.dataset.inlineEligible === "true";
          const shouldHide = instance.state.inlineUpsellHidden || !eligible;
          inlineUpsell.classList.toggle("is-hidden", shouldHide);
        }

        const sheet = instance.root.querySelector("[data-upsell-sheet]");
        if (sheet) {
          sheet.classList.toggle("is-visible", instance.state.showUpsellSheet);
          // Empty cart = end of this cart episode. Release the frozen shown-tier so the
          // next cart starts fresh instead of inheriting the previous episode's tier.
          if (parseInt(sheet.dataset.cartItemCount || "0", 10) === 0) {
            clearFrozenShownTier();
          }
        }

        instance.applyDiscountState();
      },
      bind: () => {
        if (instance.root.dataset.cartDrawerInit === "true") return;
        instance.root.dataset.cartDrawerInit = "true";

        instance.root.addEventListener("click", (event) => {
          if (!event.target.closest(".cd-panel") && !event.target.closest("[data-upsell-sheet]")) {
            instance.close();
            return;
          }

          const closeBtn = event.target.closest("[data-cart-drawer-close]");
          if (closeBtn) {
            event.preventDefault();
            instance.close();
            return;
          }

          const removeBtn = event.target.closest("[data-cart-remove]");
          if (removeBtn) {
            event.preventDefault();
            const key = removeBtn.dataset.lineKey;
            const line = parseInt(removeBtn.dataset.line || "0", 10);
            if (key) {
              instance.removeLineByKey(key);
            } else if (line) {
              instance.updateLine(line, 0);
            }
            return;
          }

          const minusBtn = event.target.closest("[data-qty-minus]");
          if (minusBtn) {
            event.preventDefault();
            const line = parseInt(minusBtn.dataset.line || "0", 10);
            const item = minusBtn.closest("[data-cart-item]");
            const qtyEl = item && item.querySelector("[data-qty-value]");
            const current = qtyEl ? parseInt(qtyEl.textContent || "0", 10) : 0;
            if (line) {
              if (current > 1) {
                instance.updateLine(line, current - 1);
              } else {
                instance.updateLine(line, 0);
              }
            }
            return;
          }

          const plusBtn = event.target.closest("[data-qty-plus]");
          if (plusBtn) {
            event.preventDefault();
            const line = parseInt(plusBtn.dataset.line || "0", 10);
            const item = plusBtn.closest("[data-cart-item]");
            const qtyEl = item && item.querySelector("[data-qty-value]");
            const current = qtyEl ? parseInt(qtyEl.textContent || "0", 10) : 0;
            if (line) {
              instance.updateLine(line, current + 1);
            }
            return;
          }

          const clearBtn = event.target.closest("[data-clear-cart]");
          if (clearBtn) {
            event.preventDefault();
            instance.clearCart();
            return;
          }

          // Ship bar upsell - Add button
          const shipAdd = event.target.closest("[data-ship-add]");
          if (shipAdd) {
            event.preventDefault();
            var shipForm = shipAdd.closest("[data-ship-form]");
            var shipVariant = shipForm ? shipForm.querySelector("[data-ship-variant]") : null;
            if (shipVariant) {
              shipAdd.textContent = "...";
              fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ items: [{ id: parseInt(shipVariant.value), quantity: 1 }] })
              }).then(function() {
                shipAdd.textContent = "✓";
                instance.refreshSection(instance.sectionId);
              });
            }
            return;
          }

          // Ship bar upsell - Next button
          const shipNext = event.target.closest("[data-ship-next]");
          if (shipNext) {
            event.preventDefault();
            var shipEl = shipNext.closest("[data-ship-upsell]");
            if (shipEl) {
              var items = shipEl.dataset.shipItems.split("|||");
              var cur = parseInt(shipEl.dataset.shipCurrent || "0");
              cur = (cur + 1) % items.length;
              shipEl.dataset.shipCurrent = cur;
              var parts = items[cur].split("::");
              var textEl = shipEl.querySelector("[data-ship-text]");
              var varEl = shipEl.querySelector("[data-ship-variant]");
              var addBtn = shipEl.querySelector("[data-ship-add]");
              if (textEl) textEl.innerHTML = 'Add <strong>' + parts[0].substring(0, 18) + '</strong> ' + (parseInt(parts[1]) / 100).toFixed(2).replace(/^/, '£') + ' for free delivery';
              if (varEl) varEl.value = parts[2];
              if (addBtn) addBtn.textContent = "+";
            }
            return;
          }

          const inlineDismiss = event.target.closest("[data-inline-upsell-dismiss]");
          if (inlineDismiss) {
            event.preventDefault();
            // Hide whichever inline section contains this button
            var inlineParent = inlineDismiss.closest("[data-inline-upsell], [data-slim-upsell]");
            if (inlineParent) inlineParent.classList.add("is-hidden");
            instance.state.inlineUpsellHidden = true;
            writeSessionFlag(INLINE_DISMISSED_KEY, true);
            instance.applyState();
            return;
          }

          const upsellDismiss = event.target.closest("[data-upsell-dismiss]");
          if (upsellDismiss) {
            event.preventDefault();
            const dismissedSheet = instance.root.querySelector("[data-upsell-sheet]");
            const isSkipToCheckout = upsellDismiss.classList.contains("cd-upsell-skip");

            if (isSkipToCheckout) {
              // "No thanks, continue to checkout" is intent-to-buy, NOT a rejection of
              // the offer. Fire a distinct cart_upsell_checkout event (all 3 channels)
              // so the dismiss metric isn't polluted by shoppers heading to pay. We do
              // NOT set the dismissed flag or the frequency cap — nothing was rejected.
              publishUpsellEvent("cart_upsell_checkout", {
                ...collectSheetContext(dismissedSheet),
                surface: "sheet",
              });
              stampCartAttributes({
                _upsell_checkout: "1",
                _upsell_checkout_at: new Date().toISOString(),
              });
              instance.clearUpsellTimer();
              // Defer the nav a tick so the event/paint lands before the page unloads
              // (on mobile the browser otherwise starts unloading mid-update).
              requestAnimationFrame(() => {
                window.location.href = getCheckoutUrl();
              });
              return;
            }

            // Close X (cd-upsell-close) or drag handle (cd-upsell-handle) = a genuine
            // rejection of the offer.
            const dismissedVia = upsellDismiss.classList.contains("cd-upsell-close")
              ? "close_button"
              : "handle";
            publishUpsellEvent("cart_upsell_dismissed", {
              ...collectSheetContext(dismissedSheet),
              surface: "sheet",
              dismissed_via: dismissedVia,
            });
            stampCartAttributes({
              _upsell_dismissed: "1",
              _upsell_dismissed_via: dismissedVia,
              _upsell_dismissed_at: new Date().toISOString(),
            });
            instance.state.hasDismissedUpsell = true;
            writeSessionFlag(SHEET_DISMISSED_KEY, true);
            setUpsellCap(); // cross-session: don't re-show the sheet for UPSELL_CAP_DAYS
            instance.hideUpsell();
            // Show the slim fallback inline upsell
            var slimEl = instance.root.querySelector("[data-slim-upsell]");
            if (!slimEl) slimEl = document.querySelector("[data-slim-upsell]");
            if (slimEl) {
              slimEl.classList.remove("is-hidden");
              slimEl.style.display = "block";
            }
            return;
          }

          const discountToggle = event.target.closest("[data-discount-toggle]");
          if (discountToggle) {
            event.preventDefault();
            const wrapper = discountToggle.closest("[data-discount]");
            if (wrapper) {
              const isOpen = wrapper.classList.toggle("is-open");
              discountToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
            }
            return;
          }

          const discountApply = event.target.closest("[data-discount-apply]");
          if (discountApply) {
            event.preventDefault();
            const wrapper = discountApply.closest("[data-discount]");
            if (wrapper) {
              const input = wrapper.querySelector("[data-discount-input]");
              const value = input && input.value ? input.value.trim() : "";
              if (value && isValidDiscountCode(value)) {
                // Adds the code to the cart (keeps the 2-bag deal and other codes) and redraws the
                // basket; also fires the apply_coupon smart event (Clarity + Shopify analytics + DOM).
                discountApply.disabled = true;
                applyTypedCode(value, "drawer_button");
              }
            }
            return;
          }

          const discountClear = event.target.closest("[data-discount-clear]");
          if (discountClear) {
            event.preventDefault();
            const typed = getStoredDiscount();
            setStoredDiscount("");
            publishCartEvent("clear_coupon", { surface: "drawer_button" });
            // Takes off only the code they typed; XTRA2 / SEPTEMBER stay.
            (typed ? removeCodeFromCart(typed).catch(() => {}) : Promise.resolve()).then(() =>
              refreshSection(sectionId)
            );
            return;
          }

          const checkoutBtn = event.target.closest("[data-cart-checkout]");
          if (checkoutBtn) {
            event.preventDefault();
            instance.clearUpsellTimer(); // don't let the dwell reveal fire mid-navigation
            window.location.href = getCheckoutUrl();
          }

          // Subscribe widget — radio toggle (on/off)
          const subscribeToggle = event.target.closest("[data-cart-subscribe-toggle]");
          if (subscribeToggle) {
            // Don't preventDefault — let the radio update its UI state
            const widget = subscribeToggle.closest("[data-cart-subscribe-widget]");
            if (!widget) return;
            const line = parseInt(widget.dataset.line || "0", 10);
            if (!line) return;
            let mode = subscribeToggle.dataset.cartSubscribeToggle; // "on" | "off" (radios)
            if (subscribeToggle.type === "checkbox") {
              mode = subscribeToggle.checked ? "on" : "off";
            }
            if (mode === "off") {
              instance.updateLineSellingPlan(line, "", widget);
            } else if (mode === "on") {
              const freqSelect = widget.querySelector("[data-cart-subscribe-frequency]");
              const planId = freqSelect ? freqSelect.value : "";
              if (planId) {
                instance.updateLineSellingPlan(line, planId, widget);
              } else {
                // Defensive: dropdown rendered with no value. Refresh so the
                // optimistic radio state snaps back to truth.
                refreshSection(instance.sectionId);
              }
            }
            return;
          }
        });

        // A11y: the closed panel starts inert (out of the tab order); open() removes it.
        const initPanel = instance.root.querySelector(".cd-panel");
        if (initPanel && !instance.state.isOpen) initPanel.setAttribute("inert", "");

        // A11y: Escape closes the open drawer; Tab is trapped within its panel.
        // Attached once at the document level so it works no matter where focus is
        // and survives the drawer's section re-renders (which replace .cd-panel).
        if (!window.__cdKeydownBound) {
          window.__cdKeydownBound = true;
          document.addEventListener("keydown", (event) => {
            const openRoot = document.querySelector(".cd-root.is-open");
            if (!openRoot) return;
            if (event.key === "Escape") {
              event.preventDefault();
              const closeBtn = openRoot.querySelector("[data-cart-drawer-close]");
              if (closeBtn) closeBtn.click();
              return;
            }
            if (event.key !== "Tab") return;
            const panelEl = openRoot.querySelector(".cd-panel");
            if (!panelEl) return;
            const focusables = Array.prototype.filter.call(
              panelEl.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
              ),
              (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
            );
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || !panelEl.contains(active))) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && active === last) {
              event.preventDefault();
              first.focus();
            }
          });
        }

        instance.root.addEventListener("keydown", (event) => {
          const input = event.target.closest("[data-discount-input]");
          if (!input) return;
          if (event.key === "Enter") {
            event.preventDefault();
            const value = input.value ? input.value.trim() : "";
            if (value && isValidDiscountCode(value)) {
              applyTypedCode(value, "drawer_enter");
            }
          }
        });

        instance.root.addEventListener("submit", (event) => {
          const form = event.target.closest("[data-upsell-form]");
          if (!form) return;
          event.preventDefault();
          instance.addUpsell(form);
        });

        instance.root.addEventListener("change", (event) => {
          const freqSelect = event.target.closest("[data-cart-subscribe-frequency]");
          if (freqSelect) {
            const widget = freqSelect.closest("[data-cart-subscribe-widget]");
            if (!widget) return;
            const line = parseInt(widget.dataset.line || "0", 10);
            if (!line) return;
            const planId = freqSelect.value;
            if (planId) {
              instance.updateLineSellingPlan(line, planId, widget);
            }
            return;
          }

          const select = event.target.closest("[data-variant-select]");
          if (!select) return;
          const form = select.closest("[data-upsell-form]");
          if (!form) return;
          const flavorInput = form.querySelector("[data-flavor-input]");
          if (flavorInput) {
            const option = select.options[select.selectedIndex];
            flavorInput.value = option ? option.textContent.trim() : "";
          }
        });

        // Footer mouseenter/touchstart no longer force-opens the sheet — it made the
        // upsell pop on any incidental footer contact (interruptive). Reveal is now
        // dwell + 60%-scroll only.

        // Scroll-triggered upsell
        const scrollArea = instance.root.querySelector(".cd-scroll");
        if (scrollArea) {
          scrollArea.addEventListener("scroll", () => {
            instance.triggerUpsellOnScroll();
          }, { passive: true });
        }

        // Swipe-to-remove touch handlers
        instance.root.querySelectorAll("[data-swipe-item]").forEach((swipeItem) => {
          const card = swipeItem.querySelector("[data-swipe-card]");
          if (!card) return;

          let startX = 0;
          let startY = 0;
          let offsetX = 0;
          let isDragging = false;
          let hasHaptic = false;
          const MAX_SWIPE = -140;
          const ARM_THRESHOLD = -110;

          const handleTouchStart = (e) => {
            if (e.target.closest("[data-quantity-wrapper]")) return;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            offsetX = 0;
            hasHaptic = false;
            isDragging = true;
            swipeItem.classList.add("is-swiping");
            swipeItem.classList.remove("is-armed");
            card.classList.add("is-swiping");
            card.classList.remove("is-snapping");
          };

          const handleTouchMove = (e) => {
            if (!isDragging) return;
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            const diffX = currentX - startX;
            const diffY = currentY - startY;

            if (Math.abs(diffY) > Math.abs(diffX)) return;

            const clamped = Math.min(0, Math.max(diffX, MAX_SWIPE));
            if (clamped === 0) {
              offsetX = 0;
              swipeItem.classList.remove("is-armed");
              card.style.transform = "translateX(0)";
              return;
            }
            if (e.cancelable) e.preventDefault();
            offsetX = clamped;
            card.style.transform = `translateX(${offsetX}px)`;
            const armed = offsetX <= ARM_THRESHOLD;
            swipeItem.classList.toggle("is-armed", armed);
            if (armed && !hasHaptic && "vibrate" in navigator) {
              navigator.vibrate(10);
              hasHaptic = true;
            }
          };

          const handleTouchEnd = () => {
            if (!isDragging) return;
            isDragging = false;
            swipeItem.classList.remove("is-swiping");
            card.classList.remove("is-swiping");
            card.classList.add("is-snapping");

            if (offsetX <= ARM_THRESHOLD) {
              card.style.transform = "translateX(-500px)";
              const key = swipeItem.dataset.lineKey;
              const line = parseInt(swipeItem.dataset.line || "0", 10);
              setTimeout(() => {
                if (key) instance.removeLineByKey(key);
                else if (line) instance.updateLine(line, 0);
              }, 300);
            } else {
              card.style.transform = "translateX(0)";
              swipeItem.classList.remove("is-armed");
            }
            offsetX = 0;
          };

          swipeItem.addEventListener("touchstart", handleTouchStart, { passive: true });
          swipeItem.addEventListener("touchmove", handleTouchMove, { passive: false });
          swipeItem.addEventListener("touchend", handleTouchEnd);
          swipeItem.addEventListener("touchcancel", handleTouchEnd);
        });
      },
      removeLineByKey: (key) => {
        if (!key) return;
        instance.cartDebouncer(async () => {
          try {
            // Remove by stable line-item key (idempotent) rather than positional
            // index, so a stray double-call can never cascade onto a shifted line.
            const res = await fetchWithTimeout("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ id: key, quantity: 0 }),
              credentials: "same-origin",
            });
            try { if (res && res.ok) checkUpsellRemoval(await res.clone().json()); } catch (_) {}
            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Cart remove timed out");
            }
          }
        });
      },
      updateLine: (line, quantity) => {
        instance.cartDebouncer(async () => {
          try {
            const res = await fetchWithTimeout("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ line, quantity }),
              credentials: "same-origin",
            });
            try { if (res && res.ok) checkUpsellRemoval(await res.clone().json()); } catch (_) {}

            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Cart update timed out");
            }
          }
        });
      },
      updateLineSellingPlan: (line, sellingPlanId, widget) => {
        instance.cartDebouncer(async () => {
          if (widget) widget.classList.add("is-loading");
          const previousPlanId = widget ? (widget.dataset.currentPlanId || "") : "";
          const isToggleOn = sellingPlanId && sellingPlanId !== "";
          const isToggleOff = sellingPlanId === "" && previousPlanId !== "";
          const isFreqChange = isToggleOn && previousPlanId !== "" && previousPlanId !== sellingPlanId;
          try {
            const body = { line, selling_plan: sellingPlanId === null || sellingPlanId === undefined ? "" : sellingPlanId };
            const res = await fetchWithTimeout("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify(body),
              credentials: "same-origin",
            });
            if (!res.ok) throw new Error("Subscribe toggle failed: " + res.status);

            // Analytics — published BEFORE the section refresh wipes the DOM
            const ctx = collectSubscribeContext(widget);
            if (isFreqChange) {
              publishCartEvent("subscribe_frequency_changed", { ...ctx, from: previousPlanId, to: sellingPlanId });
            } else if (isToggleOn) {
              publishCartEvent("subscribe_toggled_on", { ...ctx, plan_id: sellingPlanId });
              stampCartAttributes({
                _subscribe_offered: "1",
                _subscribe_converted: "1",
                _subscribe_converted_at: new Date().toISOString(),
              });
            } else if (isToggleOff) {
              publishCartEvent("subscribe_toggled_off", ctx);
            }

            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
              // Update cumulative converted-count after the refresh shows new state
              const count = countSubscribedLines(next.root);
              if (count > 0) {
                stampCartAttributes({ _subscribe_converted_count: String(count) });
              }
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Subscribe toggle timed out");
            } else {
              console.warn("Subscribe toggle failed", e);
            }
            // On failure, force a re-render so the radio's optimistic checked
            // state snaps back to the true cart state. Without this the radio
            // sits stuck on the user's failed click.
            try {
              await refreshSection(instance.sectionId);
              const next = instanceStore.get(instance.sectionId);
              if (next) {
                next.state.isOpen = true;
                next.applyState();
              }
            } catch (_) {
              if (widget) widget.classList.remove("is-loading");
            }
          }
        });
      },
      clearCart: () => {
        // Destructive: empties the whole basket. Confirm before wiping (no undo).
        if (typeof window.confirm === "function" && !window.confirm("Clear your whole basket? This can't be undone.")) return;
        instance.cartDebouncer(async () => {
          try {
            await fetchWithTimeout("/cart/clear.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              credentials: "same-origin",
            });
            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Cart clear timed out");
            }
          }
        });
      },
      addUpsell: (form) => {
        const formData = new FormData(form);
        // Capture attribution context BEFORE the section refresh wipes the DOM.
        const card = form.closest("[data-upsell-card]");
        const sheet = form.closest("[data-upsell-sheet]") || instance.root.querySelector("[data-upsell-sheet]");
        // A pair offer posts items[][id]; record both variant ids joined with "+".
        const variantId = (form.querySelector('[name="id"]') || {}).value
          || Array.from(form.querySelectorAll('[name="items[][id]"]')).map((i) => i.value).join("+")
          || "";
        const addedHandles = ((card && card.dataset.productHandles) || (card && card.dataset.productHandle) || "")
          .split(",").map((h) => h.trim()).filter(Boolean);
        const quantity = parseInt((form.querySelector('[name="quantity"]') || {}).value || "1", 10);
        const flavorInput = form.querySelector("[data-flavor-input]");
        const flavor = flavorInput ? (flavorInput.value || "") : "";
        const cardCtx = collectCardContext(card);
        const sheetCtx = collectSheetContext(sheet);
        instance.cartDebouncer(async () => {
          try {
            const response = await fetchWithTimeout("/cart/add.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              body: formData,
              credentials: "same-origin",
            });
            if (!response.ok) throw new Error("Upsell add failed: " + response.status);
            // Publish AFTER success so we only count actually-added items.
            // Shopify also fires its own product_added_to_cart event for the same
            // /cart/add.js call; this custom event adds upsell attribution
            // (tier, slot position, surface) that Shopify's event lacks.
            publishUpsellEvent("cart_upsell_added", {
              ...cardCtx,
              cart_tier: sheetCtx.tier,
              cart_remaining_cents_before_add: sheetCtx.remaining_cents,
              cart_subtotal_cents_before_add: sheetCtx.cart_subtotal_cents,
              variant_id: variantId,
              flavor: flavor,
              quantity: quantity,
              surface: "sheet",
            });
            stampCartAttributes({
              _upsell_added: "1",
              _upsell_added_tier: cardCtx.tier || "",
              _upsell_added_position: String(cardCtx.position || ""),
              _upsell_added_product: cardCtx.product_handle || "",
              _upsell_added_variant_id: String(variantId || ""),
              _upsell_added_at: new Date().toISOString(),
            });
            rememberAddedHandles(addedHandles);
            await refreshSection(instance.sectionId);
            const next = instanceStore.get(instance.sectionId);
            if (next) {
              next.state.isOpen = true;
              next.applyState();
              next.scheduleUpsell();
            }
          } catch (e) {
            if (e.name === "AbortError") {
              console.warn("Upsell add timed out");
            }
          }
        });
      },
    };

    // Create debouncer for this instance
    instance.cartDebouncer = createDebouncer();

    instanceStore.set(sectionId, instance);
    instance.bind();
    instance.applyState();
  };

  const initAll = () => {
    document.querySelectorAll("[data-cart-drawer]").forEach((root) => {
      initCartDrawer(root);
    });
  };

  bindGlobal();
  document.addEventListener("cart:refresh", () => {
    const instance = getDefaultInstance();
    if (instance) {
      refreshSection(instance.sectionId);
    }
  });
  document.addEventListener("DOMContentLoaded", initAll);

  // Arrow cycle for inline upsell line
  document.addEventListener("click", function(e) {
    var btn = e.target.closest("[data-upsell-next]");
    if (!btn) return;
    e.preventDefault();
    var line = btn.closest("[data-slim-upsell]");
    if (!line) return;
    var raw = line.dataset.upsellItems || "";
    var items = raw.split("|||").map(function(s) { var p = s.split("::"); return { title: p[0], price: parseInt(p[1] || "0", 10), vid: p[2] }; });
    if (items.length < 2) return;
    var cur = parseInt(line.dataset.upsellCurrent || "0", 10);
    cur = (cur + 1) % items.length;
    line.dataset.upsellCurrent = cur;
    var item = items[cur];
    var textEl = line.querySelector("[data-upsell-text]");
    var vidEl = line.querySelector("[data-upsell-variant]");
    if (textEl) {
      var priceStr = (item.price / 100).toFixed(2).replace(/\.00$/, "");
      var shortTitle = item.title.length > 16 ? item.title.substring(0, 16) : item.title;
      textEl.innerHTML = 'Add <strong>' + shortTitle + '</strong> <strong class="cd-upsell-sub-price">\u00A3' + priceStr + '</strong> <span class="cd-upsell-sub-free">free delivery</span>';
    }
    if (vidEl) vidEl.value = item.vid;
  });

  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!section) return;
    const root = section.querySelector("[data-cart-drawer]");
    if (root) {
      initCartDrawer(root);
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // 3P → Bundle swap strip (BC-2). Delegated so it survives drawer re-renders.
  //   • (i) toggles the "what's in the stack" panel
  //   • Swap = true swap: add the bundle, remove the ON tub line, refresh
  //   • Dismiss hides the strip and persists per session
  // ─────────────────────────────────────────────────────────────────────────
  const cbswDismissed = () => {
    try { return sessionStorage.getItem("cbswDismissed") === "1"; } catch (e) { return false; }
  };
  const hideDismissedCbsw = () => {
    if (!cbswDismissed()) return;
    document.querySelectorAll(".cbsw").forEach((el) => { el.style.display = "none"; });
  };
  document.addEventListener("cart:refresh", () => { setTimeout(hideDismissedCbsw, 400); });
  hideDismissedCbsw();

  document.addEventListener("click", (e) => {
    const iBtn = e.target.closest("[data-cbsw-i]");
    if (iBtn) {
      const cbswEl = iBtn.closest(".cbsw");
      const panel = cbswEl && cbswEl.querySelector("[data-cbsw-panel]");
      if (panel) {
        const open = panel.classList.toggle("is-open");
        iBtn.setAttribute("aria-expanded", open ? "true" : "false");
        const sbtn = cbswEl.querySelector("[data-cbsw-swap]");
        const slbl = sbtn && sbtn.querySelector("[data-cbsw-label]");
        if (slbl) slbl.textContent = open ? (sbtn.getAttribute("data-cta-add") || "Swap") : (sbtn.getAttribute("data-cta-pick") || "Pick");
      }
      return;
    }
    const dis = e.target.closest("[data-cbsw-dismiss]");
    if (dis) {
      const strip = dis.closest(".cbsw");
      if (strip) strip.style.display = "none";
      try { sessionStorage.setItem("cbswDismissed", "1"); } catch (err) {}
      publishUpsellEvent("bundle_swap_dismissed", {
        surface: "cart",
        line_key: strip ? strip.getAttribute("data-line-key") || "" : "",
        bundle_id: strip ? strip.getAttribute("data-bundle-id") || "" : "",
      });
      return;
    }
    const sw = e.target.closest("[data-cbsw-swap]");
    if (sw) {
      const strip = sw.closest(".cbsw");
      if (!strip) return;
      const bundleId = parseInt(strip.getAttribute("data-bundle-id"), 10);
      const lineKey = strip.getAttribute("data-line-key");
      if (!bundleId || !lineKey) return;
      // Single-product swap: the flavour <select> carries the chosen variant id.
      const vsel = strip.querySelector("[data-cbsw-variant]");
      const addId = (vsel && vsel.value) ? parseInt(vsel.value, 10) : bundleId;
      // Legacy bundle mode: first click reveals the flavour pickers; the swap happens on the next click.
      const cpanel = strip.querySelector("[data-cbsw-panel]");
      if (cpanel && !cpanel.classList.contains("is-open")) {
        cpanel.classList.add("is-open");
        const cinfo = strip.querySelector("[data-cbsw-i]");
        if (cinfo) cinfo.setAttribute("aria-expanded", "true");
        const olbl = sw.querySelector("[data-cbsw-label]");
        if (olbl) olbl.textContent = sw.getAttribute("data-cta-add") || "Swap";
        try { cpanel.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e2) {}
        return;
      }
      publishUpsellEvent("bundle_swap_clicked", { surface: "cart", line_key: lineKey, bundle_id: String(addId) });
      sw.disabled = true;
      const orig = sw.textContent;
      sw.textContent = "Swapping…";
      // Human-readable swap context (the snippet exposes these as data-* on the strip).
      const swFrom = strip.getAttribute("data-swap-from") || "";
      const swToBase = strip.getAttribute("data-swap-to") || "";
      const swSaving = strip.getAttribute("data-swap-saving") || "";
      const swFlavour = (vsel && vsel.selectedOptions && vsel.selectedOptions[0]) ? vsel.selectedOptions[0].textContent.trim() : "";
      const swTo = swFlavour ? (swToBase + " (" + swFlavour + ")") : swToBase;
      // Single-product mode adds the chosen flavour variant (addId).
      // Legacy bundle mode also sends component flavours as line-item properties.
      const cprops = {};
      if (!vsel) {
        (sw.getAttribute("data-cbsw-props") || "").split("||").forEach((pair) => { if (!pair) return; const kv = pair.split("::"); if (kv.length >= 2) cprops[kv[0]] = kv.slice(1).join("::"); });
        strip.querySelectorAll("[data-cbsw-flav]").forEach((sel) => { const k = sel.getAttribute("data-cbsw-key"); if (k && sel.value) cprops[k] = sel.value; });
      }
      // Hidden line-item attribution (underscore keys stay hidden from the customer) so the
      // swapped-in line is identifiable in order line-item exports, not just cart note_attributes.
      cprops._swap_source = "ON_GSW";
      if (swFrom) cprops._swapped_from = swFrom;
      const cItem = { id: addId, quantity: 1, properties: cprops };
      (async () => {
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [cItem] }),
            credentials: "same-origin",
          });
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
            credentials: "same-origin",
          });
          stampCartAttributes({ _swap_source: "ON_GSW", _swap_surface: "cart", _swap_from: swFrom, _swap_to: swTo, _swap_saving: swSaving, _swap_target: String(addId), _swap_completed_at: new Date().toISOString() });
          publishUpsellEvent("bundle_swap_accepted", { surface: "cart", line_key: lineKey, bundle_id: String(addId), from: swFrom, to: swTo, saving: swSaving, value: parseFloat(swSaving) || 0 });
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        } catch (err) {
          sw.disabled = false;
          sw.textContent = orig;
        }
      })();
      return;
    }
  });
})();
